/**
 * useMockChat
 *
 * Local-only state for the `MockChatPage` dev harness. Owns:
 * - a seeded array of `MockedChatMessageEntity` "messages" sized to overflow
 *   the viewport so the bottom-anchor-on-mount behavior of
 *   `VirtualList(reversed)` is observable;
 * - `sendMessage(text)` which appends the trimmed `text` (no-op on blank) —
 *   exercises the sticky-to-bottom-on-append path. Returns `true` when the
 *   message was actually appended so the caller can clear its composer; the
 *   composer draft itself is owned by `MessageInput` (uncontrolled) and is no
 *   longer round-tripped through this hook.
 * - `prependOlderMessages(count)` which synchronously injects synthetic older
 *   messages at the front of the array — exercises the preserve-offset-on-
 *   prepend path.
 *
 * Every produced message carries a stable `uniqueKey` minted from a
 * monotonically-increasing counter (ref-held, no re-render impact). The key
 * encodes its origin (`seed-`, `sent-`, `older-`) so React DevTools shows
 * where each entry came from. This is what `MockChatPage`'s `getItemKey`
 * returns — repeating the same text (e.g. sending "Ok" twice) must NOT
 * collapse two list entries into one, and the harness mirrors the
 * `{ id, text }` shape real chat messages will take in production.
 *
 * No async work, no Redux, no service, no use case — by design. This is a
 * dev-only harness; the unit-test mandate from `conventions.md` applies to
 * UseCases, and none is introduced here.
 */

import { useCallback, useRef, useState } from 'react';
import type { MockedChatMessageEntity } from '../types/mockedChatMessageEntity';

/**
 * Number of seed messages on first mount. Chosen to overflow a typical
 * desktop viewport so the bottom-anchor is visually obvious on mount and
 * older messages are scroll-up-reachable.
 */
const INITIAL_MESSAGE_COUNT = 8;

/** Synthetic older messages prepended in a single batch via `prependOlderMessages`. */
const OLDER_BATCH_SIZE = 10;

export interface UseMockChatResult {
    messages: MockedChatMessageEntity[];
    /**
     * Append a new message with the given text (trimmed). Returns `true` when
     * the message was actually appended (i.e. trimmed text is non-empty) so
     * the caller can clear its composer; returns `false` for blank input.
     */
    sendMessage: (text: string) => boolean;
    prependOlderMessages: (count?: number) => void;
}

export const useMockChat = (): UseMockChatResult => {
    // Monotonically-increasing counter shared across all message origins
    // (seed / sent / older). Held in a ref so minting a new key never causes
    // a re-render. The origin prefix is appended at the call site so the
    // resulting keys read e.g. `seed-1`, `sent-12`, `older-37` in DevTools.
    const keyCounterRef = useRef<number>(0);
    const mintKey = useCallback((origin: 'seed' | 'sent' | 'older'): string => {
        keyCounterRef.current += 1;
        return `${origin}-${keyCounterRef.current}`;
    }, []);

    // Monotonically-decreasing counter so prepended "older" messages have
    // stable, distinct text labels across multiple prepend batches (e.g.
    // "Older #-1", "Older #-2", …). Kept in a ref so it survives renders
    // without re-triggering them. Independent of `keyCounterRef` because the
    // text and the key are two different identities.
    const olderCounterRef = useRef<number>(0);

    const [messages, setMessages] = useState<MockedChatMessageEntity[]>(() =>
        Array.from({ length: INITIAL_MESSAGE_COUNT }, (_, i) => ({
            text: `Message #${i + 1}`,
            uniqueKey: mintKey('seed'),
        })),
    );

    const sendMessage = useCallback((text: string): boolean => {
        const trimmed = text.trim();
        if (trimmed.length === 0) return false;
        const next: MockedChatMessageEntity = {
            text: trimmed,
            uniqueKey: mintKey('sent'),
        };
        setMessages((prev) => [...prev, next]);
        return true;
    }, [mintKey]);

    const prependOlderMessages = useCallback(
        (count: number = OLDER_BATCH_SIZE) => {
            if (count <= 0) return;
            setMessages((prev) => {
                // Add console log "Fetching older messages..."
                console.log("Fetching older messages...");
                const older: MockedChatMessageEntity[] = [];
                for (let i = 0; i < count; i++) {
                    olderCounterRef.current -= 1;
                    older.push({
                        text: `Older #${olderCounterRef.current}`,
                        uniqueKey: mintKey('older'),
                    });
                }
                // Older messages live at the front (lowest indices) so the
                // chronological order remains oldest-at-0, newest-at-end.
                // We reverse `older` so the most recently fetched "older"
                // batch's own ordering reads naturally top-to-bottom.
                return [...older.reverse(), ...prev];
            });
        },
        [mintKey],
    );

    return {
        messages,
        sendMessage,
        prependOlderMessages,
    };
};
